import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Input from '@mui/material/Input';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { ITag } from 'app/shared/model/tag.model';
import { getEntities as getTags } from 'app/entities/tag/tag.reducer';
import { IPlaceCategory } from 'app/shared/model/place-category.model';
import { getEntities as getPlaceCategories } from 'app/entities/place-category/place-category.reducer';
import { IDestination } from 'app/shared/model/destination.model';
import { getEntities as getDestinations } from 'app/entities/destination/destination.reducer';
import { mapIdList } from 'app/shared/util/entity-utils';
import { IPlace } from 'app/shared/model/place.model';
import { getEntity, updateEntity, createEntity, reset } from './place.reducer';

export const PlaceUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const tags = useAppSelector(state => state.tag.entities);
  const placeCategories = useAppSelector(state => state.placeCategory.entities);
  const destinations = useAppSelector(state => state.destination.entities);
  const placeEntity = useAppSelector(state => state.place.entity);
  const loading = useAppSelector(state => state.place.loading);
  const updating = useAppSelector(state => state.place.updating);
  const updateSuccess = useAppSelector(state => state.place.updateSuccess);

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    navigate('/place' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({
        createdBy: account?.id,
      });
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getTags({}));
    dispatch(getPlaceCategories({}));
    dispatch(getDestinations({}));
  }, []);

  useEffect(() => {
    if (placeEntity) {
      setFormData({
        ...placeEntity,
        createdBy: placeEntity?.createdBy?.id,
        tags: placeEntity?.tags?.map(e => e.id.toString()),
        categories: placeEntity?.categories?.map(e => e.id.toString()),
        destination: placeEntity?.destination?.id,
      });
      if (placeEntity.defaultImageData) {
        setImagePreview(`data:${placeEntity.defaultImageDataContentType};base64,${placeEntity.defaultImageData}`);
      }
    }
  }, [placeEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        setFormData({
          ...formData,
          defaultImage: file.name,
          defaultImageData: base64,
          defaultImageDataContentType: file.type,
        });
        setImagePreview(URL.createObjectURL(file));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    setFormData({
      ...formData,
      defaultImage: '',
      defaultImageData: '',
      defaultImageDataContentType: '',
    });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    values.enabled = values.enabled === true;
    values.destinationSight = values.destinationSight === true;

    const entity = {
      ...placeEntity,
      ...values,
      createdBy: users.find(it => it.id.toString() === values.createdBy?.toString()),
      tags: values.tags ? mapIdList(values.tags) : [],
      categories: values.categories ? mapIdList(values.categories) : [],
      destination: destinations.find(it => it.id.toString() === values.destination?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.place.home.createOrEditLabel">Create or edit a Place</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box>
            {!isNew && (
              <TextField
                label={translate('global.field.id')}
                id="place-id"
                name="id"
                value={formData.id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              label={translate('jhipsterSampleApplicationApp.place.code')}
              id="place-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled || false}
                  onChange={handleChange('enabled')}
                  id="place-enabled"
                  name="enabled"
                  data-cy="enabled"
                />
              }
              label={translate('jhipsterSampleApplicationApp.place.enabled')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.place.icon')}
              id="place-icon"
              name="icon"
              data-cy="icon"
              value={formData.icon || ''}
              onChange={handleChange('icon')}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.destinationSight || false}
                  onChange={handleChange('destinationSight')}
                  id="place-destinationSight"
                  name="destinationSight"
                  data-cy="destinationSight"
                />
              }
              label={translate('jhipsterSampleApplicationApp.place.destinationSight')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.place.longitude')}
              id="place-longitude"
              name="longitude"
              data-cy="longitude"
              value={formData.longitude || ''}
              onChange={handleChange('longitude')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.place.latitude')}
              id="place-latitude"
              name="latitude"
              data-cy="latitude"
              value={formData.latitude || ''}
              onChange={handleChange('latitude')}
              fullWidth
            />
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {translate('jhipsterSampleApplicationApp.place.defaultImageData') || 'Default Image'}
              </Typography>
              <Input type="file" inputProps={{ accept: 'image/*' }} inputRef={fileInputRef} onChange={handleFileChange} fullWidth />
              {formData.defaultImage && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Current file: {formData.defaultImage}
                </Typography>
              )}
            </Box>
            {imagePreview && (
              <Box sx={{ mb: 2, position: 'relative' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Preview:
                </Typography>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 1, border: '1px solid #ddd' }}
                />
                <Button size="small" color="error" onClick={handleClearImage} sx={{ mt: 1 }}>
                  Clear Image
                </Button>
              </Box>
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="place-tags-label">{translate('jhipsterSampleApplicationApp.place.tags')}</InputLabel>
              <Select
                labelId="place-tags-label"
                id="place-tags"
                data-cy="tags"
                multiple
                value={formData.tags || []}
                label={translate('jhipsterSampleApplicationApp.place.tags')}
                onChange={handleSelectChange('tags')}
                renderValue={selected => {
                  const selectedTags = tags?.filter((t: any) => selected.includes(t.id.toString())) || [];
                  return selectedTags.map((t: any) => t.code).join(', ');
                }}
              >
                {tags
                  ? tags.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="place-category-label">{translate('jhipsterSampleApplicationApp.place.category')}</InputLabel>
              <Select
                labelId="place-category-label"
                id="place-category"
                data-cy="category"
                multiple
                value={formData.categories || []}
                label={translate('jhipsterSampleApplicationApp.place.category')}
                onChange={handleSelectChange('categories')}
                renderValue={selected => {
                  const selectedCats = placeCategories?.filter((c: any) => selected.includes(c.id.toString())) || [];
                  return selectedCats.map((c: any) => c.code).join(', ');
                }}
              >
                {placeCategories
                  ? placeCategories.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="place-destination-label">{translate('jhipsterSampleApplicationApp.place.destination')}</InputLabel>
              <Select
                labelId="place-destination-label"
                id="place-destination"
                name="destination"
                data-cy="destination"
                value={formData.destination || ''}
                label={translate('jhipsterSampleApplicationApp.place.destination')}
                onChange={handleSelectChange('destination')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {destinations
                  ? destinations.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/place"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PlaceUpdate;
